import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tabs,
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Row,
  Col,
  Tooltip,
  Empty
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  FileTextOutlined,
  BookOutlined,
  CompassOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { articlesAPI } from '../../utils/api';
import { moveToTrash } from '../../utils/trashApi';
import useIsMobile from '../../hooks/useIsMobile';
import { getArticleStatus, STATUS_LABELS } from '../../utils/articleStatus';
import { statusColors } from '../../styles/theme';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Secciones del Camino. Si se renombran en BD, actualizar también en el backend
// (routes/articles.js CAMINO_SECTIONS) y en la app (slugToSectionName).
const SECTION_ETAPAS = 'Camino — Etapas';
const SECTION_GALERIA = 'Camino — Galería';
const SECTION_MOCHILA = 'Camino — En mi mochila';
const SECTION_BRUJULA = 'Camino — Brújula';

const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  const cleanText = text.replace(/<[^>]*>/g, '');
  return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
};

const formatDate = (dateString) => {
  if (!dateString) return 'Sin fecha';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const getStatusTag = (article) => {
  if (article.status === 'draft') {
    return <Tag color="default" icon={<EditOutlined />}>Borrador</Tag>;
  }
  if (article.date && dayjs(article.date).isAfter(dayjs())) {
    return <Tag color="orange" icon={<ClockCircleOutlined />}>Programada</Tag>;
  }
  switch (article.status) {
    case 'published': return <Tag color="green">Publicado</Tag>;
    case 'scheduled': return <Tag color="orange" icon={<ClockCircleOutlined />}>Programada</Tag>;
    default: return <Tag color="blue">Activo</Tag>;
  }
};

// Devuelve la URL del primer media de tipo image dentro de un array media[].
const firstImageFromMedia = (media) => {
  if (!Array.isArray(media)) return null;
  const img = media.find(m => m && m.type === 'image' && m.url);
  if (img) return img.url;
  const vid = media.find(m => m && m.type === 'video' && m.thumbnail);
  return vid ? vid.thumbnail : null;
};

const CaminoArticleTable = ({ section, kindLabel, newLabel, newPath, editPathBase }) => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const isMobile = useIsMobile();

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await articlesAPI.getArticles(section);
      if (response.success) {
        const active = (response.data || []).filter(a => !a.deleted_at);
        setArticles(active);
      } else {
        message.error('Error al cargar entradas del Camino');
      }
    } catch (error) {
      console.error(`Error cargando ${section}:`, error);
      message.error('Error al cargar entradas del Camino');
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // Filtros: búsqueda + estado.
  useEffect(() => {
    let next = [...articles];
    if (filters.status) {
      next = next.filter(a => {
        if (filters.status === 'scheduled') {
          return a.status === 'scheduled' || (a.date && dayjs(a.date).isAfter(dayjs()));
        }
        return a.status === filters.status;
      });
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      next = next.filter(a =>
        a.title?.toLowerCase().includes(q) ||
        a.content?.toLowerCase().includes(q)
      );
    }
    setFiltered(next);
  }, [articles, filters]);

  const handleDelete = async (id, title) => {
    try {
      const response = await moveToTrash(id);
      if (response.success) {
        message.success(`"${truncateText(title, 40)}" movida a la papelera`);
        loadArticles();
      } else {
        message.error('Error al mover a papelera');
      }
    } catch (error) {
      console.error('Error moving to trash:', error);
      message.error('Error al mover a papelera');
    }
  };

  const handleDuplicate = async (record) => {
    try {
      const duplicated = {
        title: `${record.title} (copia)`,
        subtitle: record.subtitle,
        content: record.content,
        section: record.section,
        imageUrl: record.imageUrl,
        hasAudio: record.hasAudio,
        audioUrl: record.audioUrl,
        hasVideo: record.hasVideo,
        videoUrl: record.videoUrl,
        videoType: record.videoType,
        videoThumbnail: record.videoThumbnail,
        media: Array.isArray(record.media) ? record.media : [],
        status: 'draft',
        date: new Date().toISOString()
      };
      const response = await articlesAPI.createArticle(duplicated);
      if (response.success) {
        message.success('Entrada duplicada como borrador');
        navigate(`${editPathBase}/${response.data.id}`);
      } else {
        message.error('Error al duplicar la entrada');
      }
    } catch (error) {
      console.error('Error duplicating:', error);
      message.error('Error al duplicar la entrada');
    }
  };

  const columns = [
    {
      title: '',
      key: 'image',
      width: 150,
      render: (_, record) => {
        const src = record.imageUrl || firstImageFromMedia(record.media);
        return (
          <div style={{
            width: 160, aspectRatio: '16 / 9', backgroundColor: '#f0f0f0',
            borderRadius: 8, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {src ? (
              <img src={src} alt="thumbnail"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontSize: 24 }}>📄</span>
            )}
          </div>
        );
      }
    },
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div
          style={{ cursor: 'pointer', padding: '4px 0' }}
          onClick={() => navigate(`${editPathBase}/${record.id}`)}
        >
          <Text strong style={{ fontSize: 14 }}>{truncateText(text, 60)}</Text>
          {record.content && (
            <Paragraph type="secondary" style={{ margin: '4px 0 0 0', fontSize: 12 }}>
              {truncateText(record.content, 80)}
            </Paragraph>
          )}
          {Array.isArray(record.media) && record.media.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <PictureOutlined /> {record.media.length} medios
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (_, record) => getStatusTag(record)
    },
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
      width: 170,
      sorter: (a, b) => {
        const da = a.date ? dayjs(a.date) : dayjs(a.created_at);
        const db = b.date ? dayjs(b.date) : dayjs(b.created_at);
        return da.valueOf() - db.valueOf();
      },
      defaultSortOrder: 'descend',
      render: (date, record) => {
        const isProgrammed = date && dayjs(date).isAfter(dayjs());
        const display = date || record.created_at;
        return (
          <Text type={isProgrammed ? 'warning' : 'secondary'}
            style={{ fontSize: 12, fontWeight: isProgrammed ? 'bold' : 'normal' }}>
            {isProgrammed && '📅 '}{formatDate(display)}
          </Text>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 210,
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space>
            <Tooltip title="Información">
              <Button type="text" icon={<InfoCircleOutlined />} size="small"
                onClick={() => message.info(
                  `ID: ${record.id} | Creada: ${formatDate(record.created_at)} | Autor: ${record.author || 'Sin autor'}`
                )} />
            </Tooltip>
            <Tooltip title="Mover a papelera">
              <Popconfirm
                title="¿Mover a papelera?"
                description={`"${truncateText(record.title, 40)}" se moverá a la papelera`}
                onConfirm={() => handleDelete(record.id, record.title)}
                okText="Sí, eliminar" cancelText="Cancelar"
                okButtonProps={{ danger: true }}
              >
                <Button type="text" icon={<DeleteOutlined />} size="small" danger />
              </Popconfirm>
            </Tooltip>
          </Space>
          <Space>
            <Button size="small" className="btn-duplicar" onClick={() => handleDuplicate(record)}>
              Duplicar
            </Button>
            <Button type="primary" size="small" className="btn-editar"
              onClick={() => navigate(`${editPathBase}/${record.id}`)}>
              Editar
            </Button>
          </Space>
        </Space>
      )
    }
  ];

  const sortedForCards = [...filtered].sort((a, b) => {
    const da = a.date ? dayjs(a.date) : dayjs(a.created_at);
    const db = b.date ? dayjs(b.date) : dayjs(b.created_at);
    return db.valueOf() - da.valueOf();
  });

  // Vista móvil: la tabla obligaba a scroll horizontal y partía los títulos
  // letra a letra. Misma data y mismos handlers, otra presentación.
  const renderCaminoCard = (record) => {
    const src = record.imageUrl || firstImageFromMedia(record.media);
    const status = getArticleStatus(record);
    const palette = statusColors[status] || statusColors.draft;
    const editPath = `${editPathBase}/${record.id}`;

    return (
      <div className="cms-card" key={record.id}>
        <div className="cms-card-media" onClick={() => navigate(editPath)}>
          {src ? <img src={src} alt="" /> : <span className="cms-card-media-empty">📄</span>}
        </div>

        <div className="cms-card-title cms-card-title--link" onClick={() => navigate(editPath)}>
          {record.title}
        </div>
        {record.content && (
          <div className="cms-card-sub">{truncateText(record.content, 90)}</div>
        )}

        <div className="cms-card-chips">
          <span className="status-tag" style={{ background: palette.bg, color: palette.fg }}>
            {STATUS_LABELS[status]}
          </span>
          {Array.isArray(record.media) && record.media.length > 0 && (
            <span className="section-tag section-tag--empty">
              <PictureOutlined /> {record.media.length} medios
            </span>
          )}
          <span className="cms-card-date">{formatDate(record.date || record.created_at)}</span>
        </div>

        <div className="cms-card-actions">
          <Button type="primary" icon={<EditOutlined />} className="btn-editar cms-btn-grow"
            onClick={() => navigate(editPath)}>
            Editar
          </Button>
          <Button className="btn-duplicar cms-btn-grow" onClick={() => handleDuplicate(record)}>
            Duplicar
          </Button>
          <Popconfirm
            title="¿Mover a papelera?"
            description={`"${truncateText(record.title, 40)}" se moverá a la papelera`}
            onConfirm={() => handleDelete(record.id, record.title)}
            okText="Sí, eliminar" cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} className="btn-eliminar cms-btn-icon" aria-label="Eliminar" />
          </Popconfirm>
        </div>
      </div>
    );
  };

  return (
    <>
      <Row className="cms-header-row" justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Text type="secondary">
            {filtered.length} de {articles.length} {kindLabel.toLowerCase()}
          </Text>
        </Col>
        <Col>
          <Space className="cms-actions-inline">
            <Button icon={<ReloadOutlined />} onClick={loadArticles} loading={loading}>
              Actualizar
            </Button>
            <Button type="primary" icon={<PlusOutlined />} size="large"
              onClick={() => navigate(newPath)}>
              {newLabel}
            </Button>
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Buscar por título o contenido…"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="Filtrar por estado"
              className="filter-select"
              style={{ width: 170 }}
              value={filters.status || undefined}
              onChange={(value) => setFilters(p => ({ ...p, status: value }))}
              allowClear
            >
              <Option value="published">Publicado</Option>
              <Option value="scheduled">Programada</Option>
              <Option value="draft">Borrador</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {isMobile ? (
        <div className="cms-cards">
          {loading ? (
            <Card loading />
          ) : filtered.length === 0 ? (
            <Card>
              <Empty description={`No hay ${kindLabel.toLowerCase()} aún`} />
            </Card>
          ) : (
            sortedForCards.map(renderCaminoCard)
          )}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty description={`No hay ${kindLabel.toLowerCase()} aún`} /> }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`
          }}
          scroll={{ x: 800 }}
        />
      )}
    </>
  );
};

const TAB_STORAGE_KEY = 'camino_dashboard_tab';
const VALID_TABS = ['mochila', 'etapas', 'brujula', 'galerias'];

const CaminoDashboard = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(TAB_STORAGE_KEY);
    return VALID_TABS.includes(saved) ? saved : 'mochila';
  });

  const handleTabChange = (key) => {
    setActiveTab(key);
    localStorage.setItem(TAB_STORAGE_KEY, key);
  };

  const items = [
    {
      key: 'mochila',
      label: <span><BookOutlined /> En mi mochila</span>,
      children: (
        <CaminoArticleTable
          section={SECTION_MOCHILA}
          kindLabel="Materiales"
          newLabel="Nuevo Material"
          newPath="/camino/mochila/nueva"
          editPathBase="/camino/mochila"
        />
      )
    },
    {
      key: 'etapas',
      label: <span><FileTextOutlined /> Etapas</span>,
      children: (
        <CaminoArticleTable
          section={SECTION_ETAPAS}
          kindLabel="Etapas"
          newLabel="Nueva Etapa"
          newPath="/camino/etapa/nueva"
          editPathBase="/camino/etapa"
        />
      )
    },
    {
      key: 'brujula',
      label: <span><CompassOutlined /> Brújula</span>,
      children: (
        <CaminoArticleTable
          section={SECTION_BRUJULA}
          kindLabel="Entradas"
          newLabel="Nueva Entrada"
          newPath="/camino/brujula/nueva"
          editPathBase="/camino/brujula"
        />
      )
    },
    {
      key: 'galerias',
      label: <span><PictureOutlined /> Galerías</span>,
      children: (
        <CaminoArticleTable
          section={SECTION_GALERIA}
          kindLabel="Galerías"
          newLabel="Nueva Galería"
          newPath="/camino/galeria/nueva"
          editPathBase="/camino/galeria"
        />
      )
    }
  ];

  return (
    <div className="camino-page" style={{ padding: 24, maxWidth: 1400, margin: '0 auto', textAlign: 'left' }}>
      <Title level={2} style={{ marginBottom: 4 }}>Camino de Santiago</Title>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Etapas del día y galerías del Camino (20–27 julio).
      </Paragraph>
      <Tabs className="cms-tabs-grid" activeKey={activeTab} onChange={handleTabChange} items={items} />
    </div>
  );
};

export default CaminoDashboard;
